import { Pin, PinOff, Plus, Search, Trash2, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FormSection } from '@nucleus-commerce/admin-kit/forms';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@nucleus-commerce/ui/components/alert-dialog';
import { Button } from '@nucleus-commerce/ui/components/button';
import { Input } from '@nucleus-commerce/ui/components/input';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';
import { Switch } from '@nucleus-commerce/ui/components/switch';

import { ProductPicker } from '@/features/resource-pickers';

import { useProductsByIds } from '../hooks/use-products-by-ids';
import {
  MAX_SCHEDULE_MEMBERS,
  type PoolProductForm,
  type ScheduleFormInput,
} from '../schemas/schedule-form-schema';

/** Rows per page of the picked list: a pool can hold 5,000. */
const PAGE = 50;
/** "Clear all" asks first above this many. */
const CONFIRM_OVER = 20;

function PickedTitle({
  id,
  title,
  loading,
}: {
  id: string;
  title: string | undefined;
  loading: boolean;
}) {
  const { t } = useTranslation('schedules');
  if (title) return <span className="truncate font-medium">{title}</span>;
  if (loading) return <Skeleton className="h-4 w-40" />;
  return <span className="truncate font-medium">{t('pool.unknownProduct', { id })}</span>;
}

interface Row {
  product: PoolProductForm & { key: string };
  index: number;
}

/**
 * The hand-picked pool on the create page: add from the master catalog, search what is picked,
 * pin the ones that stay live on every run (one by one or all the matches at once), remove.
 * Products handed over from the products list arrive here unpinned. The list is paged, so a pool
 * of thousands stays quick.
 */
export function PoolPickerSection({ disabled }: { disabled?: boolean }) {
  const { t } = useTranslation(['schedules', 'common']);
  const form = useFormContext<ScheduleFormInput>();
  const products = useFieldArray({ control: form.control, name: 'products', keyName: 'key' });
  const [picking, setPicking] = useState(false);
  const [q, setQ] = useState('');
  const [page, setPage] = useState(0);
  const [clearing, setClearing] = useState<number[] | null>(null);
  // Handles of products picked here; the form keeps only id, title and pin.
  const [handles, setHandles] = useState<ReadonlyMap<string, string>>(new Map());
  // Ids handed over in the URL arrive without a title: read them in `?ids=` batches, once.
  const [handedOver] = useState(() =>
    form
      .getValues('products')
      .filter((p) => p.title === null)
      .map((p) => p.id),
  );
  const lookup = useProductsByIds(handedOver);
  const titleOf = (product: PoolProductForm) =>
    product.title ?? lookup.data?.get(product.id)?.title;
  const handleOf = (id: string) => handles.get(id) ?? lookup.data?.get(id)?.handle;
  const picked = useMemo(() => new Set(products.fields.map((p) => p.id)), [products.fields]);
  const pinned = products.fields.filter((p) => p.pinned).length;
  const error = form.formState.errors.products;

  const needle = q.trim().toLowerCase();
  const matches = useMemo<Row[]>(() => {
    const rows = products.fields.map((product, index) => ({ product, index }));
    if (!needle) return rows;
    return rows.filter(({ product }) =>
      [
        product.title ?? lookup.data?.get(product.id)?.title,
        handles.get(product.id) ?? lookup.data?.get(product.id)?.handle,
        product.id,
      ].some((text) => text?.toLowerCase().includes(needle)),
    );
  }, [handles, lookup.data, needle, products.fields]);
  const pages = Math.max(1, Math.ceil(matches.length / PAGE));
  const current = Math.min(page, pages - 1);
  const rows = matches.slice(current * PAGE, current * PAGE + PAGE);
  const searching = needle !== '';

  /** Pins or unpins every match (every product without a search). */
  const setPinned = (value: boolean) => {
    const targets = new Set(matches.map((row) => row.index));
    products.replace(
      products.fields.map(({ key: _key, ...product }, index) =>
        targets.has(index) ? { ...product, pinned: value } : product,
      ),
    );
  };
  const removeIndexes = (indexes: number[]) => {
    products.remove(indexes);
    setPage(0);
  };
  const askClear = () => {
    const indexes = matches.map((row) => row.index);
    if (indexes.length > CONFIRM_OVER) setClearing(indexes);
    else removeIndexes(indexes);
  };

  return (
    <FormSection
      title={t('sections.pool')}
      description={t('sections.poolDescription')}
      tour="schedules.form.pool"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm" role="status">
          {t('pool.pickedCount', { count: products.fields.length })}
          {pinned > 0 ? (
            <span className="text-muted-foreground">
              {' · '}
              {t('columns.pinnedCount', { count: pinned })}
            </span>
          ) : null}
        </p>
        {disabled ? null : (
          <Button type="button" variant="outline" size="sm" onClick={() => setPicking(true)}>
            <Plus aria-hidden />
            {t('pool.add')}
          </Button>
        )}
      </div>
      {products.fields.length > 0 ? (
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative min-w-56 flex-1">
            <Search
              aria-hidden
              className="text-muted-foreground absolute top-1/2 left-2.5 size-4 -translate-y-1/2"
            />
            <Input
              type="search"
              value={q}
              onChange={(e) => {
                setQ(e.target.value);
                setPage(0);
              }}
              placeholder={t('pool.searchPicked')}
              aria-label={t('pool.searchPicked')}
              className="pl-8"
            />
          </div>
          {disabled ? null : (
            <>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={matches.length === 0}
                onClick={() => setPinned(true)}
              >
                <Pin aria-hidden />
                {searching ? t('pool.pinMatches') : t('pool.pinAll')}
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={matches.length === 0}
                onClick={() => setPinned(false)}
              >
                <PinOff aria-hidden />
                {searching ? t('pool.unpinMatches') : t('pool.unpinAll')}
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="text-destructive hover:text-destructive"
                disabled={matches.length === 0}
                onClick={askClear}
              >
                <Trash2 aria-hidden />
                {searching ? t('pool.clearMatches') : t('pool.clearAll')}
              </Button>
            </>
          )}
        </div>
      ) : null}
      {products.fields.length === 0 ? (
        <p className="text-muted-foreground text-sm">{t('pool.emptyCreate')}</p>
      ) : rows.length === 0 ? (
        <p className="text-muted-foreground text-sm">
          {t('pool.noPickedMatches', { q: q.trim() })}
        </p>
      ) : (
        <ul className="divide-y rounded-md border" aria-label={t('sections.pool')}>
          {rows.map(({ product: row, index }) => (
            <li key={row.key} className="flex items-center gap-3 px-3 py-2 text-sm">
              <div className="min-w-0 flex-1">
                <PickedTitle id={row.id} title={titleOf(row)} loading={lookup.isLoading} />
                {handleOf(row.id) ? (
                  <code className="text-muted-foreground block truncate text-xs">
                    {handleOf(row.id)}
                  </code>
                ) : null}
              </div>
              <label className="text-muted-foreground flex items-center gap-2 text-xs">
                <Pin aria-hidden className="size-3.5" />
                {t('pool.pinned')}
                <Switch
                  checked={row.pinned}
                  disabled={disabled}
                  aria-label={t('pool.pinFor', { title: titleOf(row) ?? row.id })}
                  onCheckedChange={(checked) =>
                    products.update(index, { id: row.id, title: row.title, pinned: checked })
                  }
                />
              </label>
              {disabled ? null : (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="size-7"
                  aria-label={t('pool.removeFor', { title: titleOf(row) ?? row.id })}
                  onClick={() => products.remove(index)}
                >
                  <X aria-hidden />
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
      {matches.length > PAGE ? (
        <div className="flex items-center justify-between gap-2 text-sm">
          <span className="text-muted-foreground tabular-nums">
            {t('pool.range', {
              from: current * PAGE + 1,
              to: Math.min(matches.length, current * PAGE + PAGE),
              total: matches.length,
            })}
          </span>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={current === 0}
              onClick={() => setPage(current - 1)}
            >
              {t('pool.previousPage')}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={current >= pages - 1}
              onClick={() => setPage(current + 1)}
            >
              {t('pool.nextPage')}
            </Button>
          </div>
        </div>
      ) : null}
      {error?.message ? (
        <p className="text-destructive text-sm" role="alert">
          {error.message}
        </p>
      ) : null}
      <ul className="text-muted-foreground list-disc space-y-1 pl-5 text-xs">
        <li>{t('explain.drafts')}</li>
        <li>{t('explain.newcomers')}</li>
        <li>{t('explain.pinned')}</li>
        <li>{t('explain.manualEdits')}</li>
      </ul>
      <ProductPicker
        scope={{ kind: 'org' }}
        open={picking}
        onOpenChange={setPicking}
        tour="schedules.form.pool-picker"
        title={t('pool.pickerTitle')}
        confirmLabel={t('pool.pickerConfirm')}
        multiple={MAX_SCHEDULE_MEMBERS}
        lockedIds={picked}
        lockedLabel={t('pool.alreadyPicked')}
        onConfirm={(selection) => {
          const known = new Map(selection.items.map((p) => [p.id, p]));
          setHandles((prev) => {
            const next = new Map(prev);
            for (const p of selection.items) next.set(p.id, p.handle);
            return next;
          });
          const fresh = selection.ids.filter((id) => !picked.has(id));
          if (fresh.length > 0) {
            products.append(
              fresh.map((id) => ({ id, title: known.get(id)?.title ?? null, pinned: false })),
            );
          }
          setPicking(false);
        }}
      />
      <AlertDialog
        open={clearing !== null}
        onOpenChange={(open) => {
          if (!open) setClearing(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('pool.clear.title', { count: clearing?.length ?? 0 })}
            </AlertDialogTitle>
            <AlertDialogDescription>{t('pool.clear.description')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common:actions.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive hover:bg-destructive/90 text-white"
              onClick={() => {
                if (clearing) removeIndexes(clearing);
                setClearing(null);
              }}
            >
              {t('pool.clear.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </FormSection>
  );
}
